import { useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import Price, { formatPrice } from '@/components/ui/Price';
import Modal from '@/components/ui/Modal';
import EmporterKindSwitch, { type EmporterKind } from './EmporterKindSwitch';
import EmporterSourceFilter, { type EmporterFilterKey } from './EmporterSourceFilter';
import NoteCreateModals, { type CreatedTicket } from './NoteCreateModals';
import OnlineOrderDetailModal from './OnlineOrderDetailModal';
import OnlineOrderRow from './OnlineOrderRow';
import OpenTableModal from './OpenTableModal';
import NewReservationSheet from './NewReservationSheet';
import ReservationsPanel from './ReservationsPanel';
import RestaurantMark from './RestaurantMark';
import SalleBar from './SalleBar';
import TableStateFilter, { type StateFilterKey } from './TableStateFilter';
import TableTile from './TableTile';
import UnsentCartBadge from './UnsentCartBadge';
import WaiterLanguagePicker from './WaiterLanguagePicker';
import InstallAppPrompt from './installApp/InstallAppPrompt';
import BugReportRow from './bugReport/BugReportRow';
import BugReportSheet from './bugReport/BugReportSheet';
import LoyaltyScanSheet from './loyaltyScan/LoyaltyScanSheet';
import ScanIcon from './loyaltyScan/ScanIcon';
import { useWaiterLoyaltyProgramQuery } from './loyaltyScan/loyaltyProgramApi';
import { useAdaptivePolling, useObservedPollResponse } from './useAdaptivePolling';
import { chunkTableGroups, groupMergedTables, layoutChunkSpans, sortWaiterTables } from './tableSort';
import { compareNoteLabels } from './noteSort';
import { findNextPickup, sortOnlineOrdersRecentFirst } from './onlineOrderSort';
import {
  DEFAULT_RESERVATION_GRACE_MINUTES,
  reservationUndoTarget,
  visibleReservations,
} from './reservationList';
import { draftFromReservation, toDateInput, type NewReservationDraft } from './reservationQuickEntry';
import { useNow } from './useNow';
import { useTabSwipe } from './useTabSwipe';
import { useWaiterDeliveryEnabled } from './useWaiterDeliveryEnabled';
import {
  deriveTableState,
  filterTablesBySalle,
  hasPendingRound,
  MAX_TABLES_PER_SESSION,
  oneRowPerSession,
  pendingItemsCount,
  type SalleKey,
} from './tableState';
import { formatTicketTime } from './ticketTime';
import { readUnsentWaiterQty } from './waiterCartSlice';
import RegroupPicker from './RegroupPicker';
import {
  absorbedSessions,
  freedUuids,
  mergeBlockReason,
  regroupResult,
  type RegroupMode,
} from './regroupSelection';
import { useWaiterDocumentLock } from './useWaiterDocumentLock';
import type { TicketArrivalState } from './useTakeOverRound';
import {
  useWaiterAreasQuery,
  useWaiterReservationsQuery,
  usePrintWaiterReservationMutation,
  useSetWaiterReservationStatusMutation,
  useCreateWaiterReservationMutation,
  useUpdateWaiterReservationMutation,
  useWaiterTablesQuery,
  useListTakeawayQuery,
  useOnlineOrdersQuery,
  useRegroupSessionMutation,
  useUpdateSessionCoversMutation,
  useDetachTableMutation,
  type WaiterTable,
  type WaiterOnlineOrder,
  type WaiterReservation,
  type WaiterReservationStatus,
  type PendingValidationRound,
} from './waiterApi';
import { selectActiveTicket, setTicket, setUiLocked, startNameChange } from './waiterSlice';
import { useWaiterAuthGuard } from './useWaiterAuthGuard';
import type { ShowWaiterToast } from '@/features/waiterTicket/waiterToast';

type NotesTab = 'surplace' | 'emporter' | 'reservations';

const BASE_TABS: NotesTab[] = ['surplace', 'emporter'];

interface Props {
  restaurantUuid: string;
  /** SC-979 — la marque du restaurant, posée dans la rangée d'onglets.
   *  Facultative : les écrans de test montent cette page sans fiche. */
  restaurantName?: string;
  restaurantLogoUrl?: string | null;
  restaurantLogoInverseUrl?: string | null;
  token: string;
  waiterName?: string;
  waiterNames?: string[];
  /** SC-966 — the shell's toast, so « Arrivé » can offer its « Annuler »
   *  even after the screen has navigated to the freshly opened table. */
  showToast?: ShowWaiterToast;
}

const REGROUP_TAB_LABEL: Record<RegroupMode | 'detach', string> = {
  move: 'waiter.overview.tabMove',
  merge: 'waiter.overview.tabMerge',
  detach: 'waiter.overview.tabDetach',
};

function priceToCents(value: string): number {
  return Math.round(parseFloat(value) * 100);
}

function tabStorageKey(restaurantUuid: string): string {
  return `waiterNotesTab:${restaurantUuid}`;
}

function readTabPref(restaurantUuid: string): NotesTab {
  try {
    const stored = localStorage.getItem(tabStorageKey(restaurantUuid));
    if (stored === 'emporter' || stored === 'reservations') return stored;
    return 'surplace';
  } catch {
    return 'surplace';
  }
}

/** SC-1044 — l'onglet que la notification designe (`?tab=emporter`).
 *
 *  Le carnet ouvert par un appui sur « Nouvelle commande » doit s'ouvrir SUR
 *  la commande : le serveur a le telephone en main, pas le temps de chercher.
 *  Prioritaire sur la preference gardee, et seulement pour cette ouverture —
 *  l'effet qui ecrit la preference remet ensuite les deux d'accord. */
function readTabFromSearch(search: string): NotesTab | null {
  try {
    const asked = new URLSearchParams(search).get('tab');
    if (asked === 'emporter' || asked === 'reservations' || asked === 'surplace') return asked;
  } catch {
    // URLSearchParams absent : la preference gardee fait l'affaire.
  }
  return null;
}

/** SC-1274 — une notification de livraison ajoute `segment=livraison` a
 *  `?tab=emporter` : l'onglet reste celui d'avant pour un carnet qui ne
 *  connait pas encore la vue Livraison. Sans ce parametre, « Emporter ». */
function readKindFromSearch(search: string): EmporterKind {
  try {
    return new URLSearchParams(search).get('segment') === 'livraison' ? 'livraison' : 'emporter';
  } catch {
    return 'emporter';
  }
}

function writeTabPref(restaurantUuid: string, tab: NotesTab): void {
  try {
    localStorage.setItem(tabStorageKey(restaurantUuid), tab);
  } catch {
    // localStorage unavailable — persistence is a nicety, not required.
  }
}

// Spec §2.2 D1 — the salle bar's selected chip, persisted per restaurant.
function salleStorageKey(restaurantUuid: string): string {
  return `waiterSalle:${restaurantUuid}`;
}

function readSallePref(restaurantUuid: string): SalleKey {
  try {
    return localStorage.getItem(salleStorageKey(restaurantUuid)) ?? 'all';
  } catch {
    return 'all';
  }
}

function writeSallePref(restaurantUuid: string, key: SalleKey): void {
  try {
    localStorage.setItem(salleStorageKey(restaurantUuid), key);
  } catch {
    // localStorage unavailable — persistence is a nicety, not required.
  }
}

// A card entry, normalised from a takeaway note — Emporter only (§2.3, F3
// scope). Sur place no longer builds an equivalent card: every table renders
// as a `TableTile` instead (§2.2 D3), free ones included.
interface NoteCard {
  sessionUuid: string;
  kind: 'takeaway';
  label: string;
  covers: number | null;
  openedAt: string;
  runningTotal: string;
  pendingValidation: PendingValidationRound[];
}

/**
 * Spec 2026-08-28-waiter-salle-ux-design.md §2.1/§2.2 — the waiter
 * handheld's home screen: two tabs, Sur place (salles + table-state grid,
 * §2.2) and Emporter (waiter notes, §2.3 — F3 scope, left byte-identical to
 * the SC-761 shape here). Replaces TablesOverview (kept, untouched, dead).
 *
 * Laid out on the `.waiter-root` grid (header / tabs / scroll area / footer)
 * like the ticket screen; the Sur place salle bar + state filter live INSIDE
 * the scrolling tabpanel, `sticky top-0`, so the grid stays 4 rows.
 */
export default function NotesListPage({
  restaurantUuid,
  restaurantName,
  restaurantLogoUrl,
  restaurantLogoInverseUrl,
  token,
  waiterName,
  waiterNames,
  showToast,
}: Props) {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // §0 "Document scroll" — kills iOS rubber-band / Android pull-to-refresh
  // under the fixed root while this screen is mounted.
  useWaiterDocumentLock();

  const [tab, setTab] = useState<NotesTab>(
    () => readTabFromSearch(window.location.search) ?? readTabPref(restaurantUuid),
  );
  useEffect(() => {
    writeTabPref(restaurantUuid, tab);
  }, [restaurantUuid, tab]);

  const [isVisible, setIsVisible] = useState(!document.hidden);
  useEffect(() => {
    const handler = () => setIsVisible(!document.hidden);
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, []);

  const [isOnline, setIsOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  // DF-0177 — trois sondages a 10 s sur le meme ecran, c'est ce qui a fait de
  // deux tablettes 8,8 % du trafic de toute la plateforme. Chacun recule quand
  // sa reponse ne change pas ; le flux SSE de `WaiterPendingOrdersHost` porte
  // les vrais changements en invalidant ces memes tags.
  const [tablesPollMs, observeTables] = useAdaptivePolling(isVisible, 10_000, 60_000);
  const {
    data: tables,
    error: tablesError,
    refetch: refetchTables,
    fulfilledTimeStamp: tablesStamp,
  } = useWaiterTablesQuery(
    { restaurantUuid, token },
    { pollingInterval: tablesPollMs, skip: !token, refetchOnMountOrArgChange: true },
  );
  useObservedPollResponse(observeTables, tables, tablesStamp);
  const [takeawayPollMs, observeTakeaway] = useAdaptivePolling(isVisible, 10_000, 60_000);
  const {
    data: takeawayNotes,
    error: takeawayError,
    refetch: refetchTakeaway,
    fulfilledTimeStamp: takeawayStamp,
  } = useListTakeawayQuery(
    { restaurantUuid, token },
    { pollingInterval: takeawayPollMs, skip: !token, refetchOnMountOrArgChange: true },
  );
  useObservedPollResponse(observeTakeaway, takeawayNotes, takeawayStamp);
  // §2.2 D1 — salles (Area), read-only here: they are created and renamed in
  // the admin / owner dashboards, and this list only feeds SalleBar's chips.
  const { data: areas } = useWaiterAreasQuery({ restaurantUuid, token }, { skip: !token });
  // §2.3 D7 — customer take-out/delivery orders (Emporter « En ligne »),
  // polled the same way as the other two lists (spec §4).
  const [onlineOrdersPollMs, observeOnlineOrders] = useAdaptivePolling(
    isVisible,
    10_000,
    60_000,
  );
  const {
    data: onlineOrdersData,
    error: onlineOrdersError,
    refetch: refetchOnlineOrders,
    fulfilledTimeStamp: onlineOrdersStamp,
  } = useOnlineOrdersQuery(
    { restaurantUuid, token },
    { pollingInterval: onlineOrdersPollMs, skip: !token, refetchOnMountOrArgChange: true },
  );
  useObservedPollResponse(observeOnlineOrders, onlineOrdersData, onlineOrdersStamp);

  // SC-966 — les reservations du jour, relayees de hub par sale_api. Elles
  // bougent a l'heure du repas, pas a la seconde : 60 s de base, contre 10 s
  // pour les tables.
  //
  // DF-0226 — le repli de DF-0177 montait ici jusqu'a 5 min, et une liste vide
  // ne change jamais : un restaurant sans reservation du jour atteignait ce
  // plafond en quatre sondages et y restait, si bien qu'une reservation prise
  // pendant le service attendait jusqu'a 5 min pour apparaitre. Les
  // reservations sont la SEULE donnee de ce carnet sans poussee — le flux SSE
  // de `WaiterPendingOrdersHost` porte les evenements de commande de
  // `sale_api`, alors qu'une reservation nait dans `ignite-hub` — donc le
  // sondage n'est pas un filet ici, c'est le chemin nominal : plafond = base.
  const [reservationsPollMs, observeReservations] = useAdaptivePolling(isVisible, 60_000, 60_000);
  const {
    data: reservationsDay,
    error: reservationsError,
    refetch: refetchReservations,
    fulfilledTimeStamp: reservationsStamp,
  } = useWaiterReservationsQuery(
    { restaurantUuid, token },
    { pollingInterval: reservationsPollMs, skip: !token, refetchOnMountOrArgChange: true },
  );
  useObservedPollResponse(observeReservations, reservationsDay, reservationsStamp);
  // SC-1612 — un autre jour que celui du service : le client qui a réservé
  // pour la semaine prochaine rappelle. `null` = aujourd'hui, la liste que
  // compte la pastille de l'onglet, qui reste donc toujours celle du jour.
  // `currentData`, pas `data` : sinon l'ancien jour resterait affiché sous la
  // date du nouveau le temps de la réponse.
  const [resaDay, setResaDay] = useState<string | null>(null);
  const {
    currentData: otherDay,
    error: otherDayError,
    refetch: refetchOtherDay,
    fulfilledTimeStamp: otherDayStamp,
  } = useWaiterReservationsQuery(
    { restaurantUuid, token, date: resaDay ?? undefined },
    { pollingInterval: reservationsPollMs, skip: !token || !resaDay, refetchOnMountOrArgChange: true },
  );

  // DF-0226 — ecran eteint = `pollingInterval` a 0, donc zero sondage ; et le
  // retour de visibilite ne fait que re-armer l'intervalle, sans redemander.
  // Un telephone de salle passe le plus clair du service en veille : au
  // reveil, le carnet affirmait « aucune reservation » avec une reponse
  // vieille de toute la veille, pendant 60 s de plus. On redemande tout de
  // suite.
  //
  // DF-0504 — idem pour les tables et les deux listes Emporter : le ✔ d'une
  // table vers le pont de caisse ouvre un autre onglet, et le serveur revient
  // ici une fois l'envoi fait. Sans relecture, la tuile restait « occupee »
  // jusqu'au prochain tick (10 s ou plus), et la toucher rouvrait la table.
  const wasVisible = useRef(isVisible);
  useEffect(() => {
    if (isVisible && !wasVisible.current && token) {
      void refetchReservations();
      if (resaDay) void refetchOtherDay();
      void refetchTables();
      void refetchTakeaway();
      void refetchOnlineOrders();
    }
    wasVisible.current = isVisible;
  }, [isVisible, token, resaDay, refetchReservations, refetchOtherDay, refetchTables, refetchTakeaway, refetchOnlineOrders]);
  const [setReservationStatus] = useSetWaiterReservationStatusMutation();
  const [createReservation] = useCreateWaiterReservationMutation();
  const [newResaOpen, setNewResaOpen] = useState(false);
  // SC-1281 — la réservation qu'on modifie ; le même formulaire que « Nouvelle
  // réservation », ouvert sur ses valeurs.
  const [updateReservation] = useUpdateWaiterReservationMutation();
  const [printReservation] = usePrintWaiterReservationMutation();
  const [editingResa, setEditingResa] = useState<WaiterReservation | null>(null);
  const editingDraft = useMemo(
    () => (editingResa ? draftFromReservation(editingResa) : null),
    [editingResa],
  );

  // L'onglet n'existe que pour un restaurant dont le module reservation est
  // ouvert (regle utilisateur) : hub le dit, et tant qu'il ne l'a pas dit,
  // l'onglet n'est pas la.
  const reservationsEnabled = reservationsDay?.enabled === true;
  // Une reservation sort de la liste 30 min apres son heure, sans attendre le
  // sondage suivant : l'horloge bat pendant que l'onglet est visible.
  const nowMs = useNow(30_000, reservationsEnabled && isVisible);
  // SC-970 — la journee entiere, tous etats confondus : c'est le panneau qui
  // filtre. Ici on ne garde que le seau « A venir », parce que c'est lui que
  // compte la pastille de l'onglet — une resa annulee ou deja arrivee n'est
  // pas du travail qui attend.
  const resasOfTheDay = useMemo(
    () => (reservationsEnabled ? reservationsDay?.reservations ?? [] : []),
    [reservationsEnabled, reservationsDay],
  );
  const resasGraceMinutes = reservationsDay?.graceMinutes ?? DEFAULT_RESERVATION_GRACE_MINUTES;
  const visibleResas = useMemo(
    () => visibleReservations(resasOfTheDay, nowMs, resasGraceMinutes),
    [resasOfTheDay, nowMs, resasGraceMinutes],
  );

  // Spec §1.3 G4 / F2 consolidation — 10s poll above; the live-refresh half of
  // W-L-10 now comes from `WaiterPendingOrdersHost`'s single SSE stream
  // invalidating the `WaiterTables`/`Takeaway` RTK Query tags both of these
  // queries `providesTags`. This screen no longer opens a second EventSource
  // (05-regression-risks.md F-1's "exactly one stream per handheld").

  // SC-381 parity — a 401/403 on any poll means this device's waiter token
  // is dead; clear it so WaiterShell drops back to the PinPad.
  // DF-0193 — garde partagé (`useWaiterAuthGuard`).
  useWaiterAuthGuard(restaurantUuid, [tablesError, takeawayError, onlineOrdersError, reservationsError]);

  // `onlineOrdersError` deliberately does NOT feed the whole-carnet offline
  // banner (review F3#3): the `online-orders/` endpoint is new (B3) and can
  // 404/500 independently of `tables/`/`takeaway/` (e.g. a deploy-order
  // mismatch), which would otherwise paint every screen — including Sur
  // place, whose own data is fine — permanently "hors ligne". It stays in
  // the 401/403 auth-clear effect above and gets its own inline line inside
  // the En ligne section instead.
  const isOffline = !isOnline || !!tablesError || !!takeawayError;

  // review F3#7a — a bare `tables ?? []` re-creates a new array reference on
  // every render, which then makes the `salleTables` useMemo below think its
  // dependency changed every time (react-hooks/exhaustive-deps warning).
  const allTables = useMemo(() => tables ?? [], [tables]);

  // §2.2 D1 — salle selection: persisted, falls back to 'all' when the
  // stored area no longer exists (salle disparue) OR the restaurant has no
  // areas left at all (SalleBar then shows no chips — review F1F2#3).
  const [salleKey, setSalleKey] = useState<SalleKey>(() => readSallePref(restaurantUuid));
  useEffect(() => {
    writeSallePref(restaurantUuid, salleKey);
  }, [restaurantUuid, salleKey]);
  useEffect(() => {
    if (!areas) return;
    if (salleKey === 'all') return;
    if (areas.length === 0) {
      setSalleKey('all');
      return;
    }
    if (salleKey === 'none') return;
    if (!areas.some((a) => a.uuid === salleKey)) setSalleKey('all');
  }, [areas, salleKey]);

  // The filter can never outlive the control that clears it (review
  // F1F2#3): while `areas` hasn't resolved yet (undefined — e.g. the
  // `areas/` poll is erroring, which can persist indefinitely since that
  // query has no retry/polling of its own) or resolves to zero rows,
  // SalleBar renders no chips, so fall back to 'all' at the read site too —
  // the effect above only fires once `areas` actually settles.
  const effectiveSalleKey: SalleKey = areas && areas.length > 0 ? salleKey : 'all';
  const salleTables = useMemo(
    () => filterTablesBySalle(allTables, effectiveSalleKey),
    [allTables, effectiveSalleKey],
  );

  // §2.2 D2 — legend/filter: none active = show everything; tapping the
  // already-active chip clears the filter.
  const [stateFilter, setStateFilter] = useState<StateFilterKey | null>(null);
  const toggleStateFilter = (key: StateFilterKey) => setStateFilter((prev) => (prev === key ? null : key));

  const visibleTables = useMemo(() => {
    if (!stateFilter) return salleTables;
    // SC-976 design §5.1 — a member row shares its anchor's `session`
    // (same `pendingValidation`), so it would otherwise pass this filter
    // too: « À valider » ne retient que l'ancre.
    if (stateFilter === 'pending') {
      return salleTables.filter((tb) => hasPendingRound(tb) && tb.role !== 'member');
    }
    return salleTables.filter((tb) => deriveTableState(tb) === stateFilter);
  }, [salleTables, stateFilter]);
  const sortedTiles = useMemo(() => groupMergedTables(sortWaiterTables(visibleTables, 'room')), [visibleTables]);
  const tileChunks = useMemo(() => chunkTableGroups(sortedTiles), [sortedTiles]);
  // SC-976 — une tablée réunie est UNE carte (voir `groupColumnSpan`) : ses
  // tables y sont des puces, au lieu d'une tuile par table. Le nombre de
  // colonnes vient de la grille elle-même (auto-fill), pour ne jamais demander
  // plus que la rangée.
  const tableGridRef = useRef<HTMLDivElement>(null);
  const [gridColumns, setGridColumns] = useState(3);
  const chunkSpans = useMemo(() => layoutChunkSpans(tileChunks, gridColumns), [tileChunks, gridColumns]);

  // §2.2 D3 — tapping a free tile opens the covers prompt; occupied tiles
  // navigate straight to the ticket (never a POST).
  const [openTarget, setOpenTarget] = useState<WaiterTable | null>(null);
  const navigateToSession = (sessionUuid: string) => {
    navigate(`/${restaurantUuid}/serveur/ticket/${sessionUuid}`);
  };
  const handleTileTap = (tb: WaiterTable) => {
    if (tb.session === null) {
      setOpenTarget(tb);
    } else {
      navigateToSession(tb.session.uuid);
    }
  };
  // DF-0223 — la pastille rouge ouvre le ticket DIRECTEMENT sur la feuille de
  // la ronde en attente, plutot que sur l'onglet Plats d'ou le serveur ne
  // retrouvait plus Valider. Le ticket porte deja toute la mecanique
  // (Valider / Refuser / Modifier + double confirmation) : rien n'est duplique
  // ici, on lui dit seulement sur quoi ouvrir.
  const handlePendingTap = (tb: WaiterTable) => {
    if (!tb.session) return;
    const state: TicketArrivalState = { openPendingSheet: true };
    navigate(`/${restaurantUuid}/serveur/ticket/${tb.session.uuid}`, { state });
  };
  // SC-966 « Installer » — la table choisie dans la fiche de reservation passe
  // par le MEME OpenTableModal que la grille Sur place (couverts pre-remplis
  // avec le nombre de personnes, que le serveur peut corriger), et la
  // reservation est marquee arrivee une fois la table ouverte.
  const [seatFor, setSeatFor] = useState<WaiterReservation | null>(null);

  const applyReservationStatus = async (
    reservation: WaiterReservation,
    status: WaiterReservationStatus,
  ): Promise<string | null> => {
    try {
      await setReservationStatus({ reservationUuid: reservation.uuid, status, token }).unwrap();
      return null;
    } catch (err: unknown) {
      const apiErr = err as { status?: number; data?: { code?: string } };
      const code = apiErr.data?.code;
      if (code === 'ALREADY_HANDLED') return t('waiter.notes.reservations.errorAlreadyHandled');
      if (code === 'TOO_EARLY') return t('waiter.notes.reservations.errorTooEarly');
      if (code === 'NOT_TODAY') return t('waiter.notes.reservations.errorNotToday');
      // Une correction ne peut pas effacer une annulation. Si hub refuse
      // le retour proposé, la ligne a bougé sur un autre appareil.
      if (code === 'NEVER_CANCELLED' || code === 'WAS_CANCELLED') {
        return t('waiter.notes.reservations.errorRefused');
      }
      if (code === 'HUB_UNAVAILABLE') return t('waiter.notes.reservations.errorUnavailable');
      return t('waiter.notes.errors.generic');
    }
  };

  const handleReservationStatus = async (
    reservation: WaiterReservation,
    status: WaiterReservationStatus,
  ): Promise<string | null> => {
    const error = await applyReservationStatus(reservation, status);
    if (error === null && status === 'CANCELLED' && reservation.status === 'CONFIRMED') {
      showToast?.(t('waiter.notes.reservations.toastCancelled', { name: reservation.customerName }));
    }
    if (error === null && (status === 'COMPLETED' || status === 'NO_SHOW')) {
      // SC-970 — le retour en arriere du bandeau vise la ou hub le permet :
      // CANCELLED si la resa portait une annulation (le carnet ne la blanchit
      // jamais), CONFIRMED sinon.
      const undoTo = reservationUndoTarget(reservation);
      showToast?.(
        status === 'COMPLETED'
          ? t('waiter.notes.reservations.toastArrived', { name: reservation.customerName })
          : t('waiter.notes.reservations.toastNoShow', { name: reservation.customerName }),
        {
          label: t('waiter.notes.reservations.undo'),
          // Le retour en arriere peut echouer comme le reste (hub muet, ou un
          // autre appareil a deja rebouge la ligne) : le dire, sinon le
          // serveur croit la reservation revenue jusqu'au sondage suivant.
          onClick: () => {
            void applyReservationStatus(reservation, undoTo).then((undoError) => {
              if (undoError !== null) showToast?.(undoError);
            });
          },
        },
      );
    }
    return error;
  };

  // SC-971 — le client appelle. hub valide (nom + nombre de personnes), donc
  // un refus 400 est deja une phrase lisible cote serveur : on ne re-traduit
  // que ce qui n'est pas un probleme de champ.
  const handleCreateReservation = async (
    draft: NewReservationDraft,
    reservedForIso: string,
  ): Promise<string | null> => {
    if (!token) return t('waiter.notes.reservations.errorUnavailable');
    try {
      const created = await createReservation({
        token,
        customerName: draft.customerName.trim(),
        partySize: draft.partySize,
        reservedFor: reservedForIso,
        customerPhone: draft.customerPhone.trim(),
        notes: draft.notes.trim(),
      }).unwrap();
      showToast?.(
        t('waiter.notes.reservations.toastCreated', {
          name: created.customerName,
          time: formatTicketTime(created.reservedFor),
        }),
      );
      return null;
    } catch (err) {
      const data = (err as { data?: { code?: string } })?.data;
      if (data?.code === 'RESERVATION_DISABLED') {
        return t('waiter.notes.reservations.errorModuleClosed');
      }
      if (data?.code === 'HUB_UNAVAILABLE') return t('waiter.notes.reservations.errorUnavailable');
      return t('waiter.notes.reservations.errorCreate');
    }
  };

  // SC-1532 — « Imprimer » dans la fiche. Le ticket sort là où sort le ticket
  // automatique ; un restaurant qui imprime au navigateur du comptoir n'a pas
  // de sortie que le serveur puisse atteindre, et le carnet le dit.
  const handlePrintReservation = async (
    reservation: WaiterReservation,
  ): Promise<{ ok: boolean; message: string }> => {
    if (!token) return { ok: false, message: t('waiter.notes.reservations.errorUnavailable') };
    try {
      const { destination } = await printReservation({ reservationUuid: reservation.uuid, token }).unwrap();
      return {
        ok: true,
        message: t(
          destination === 'caisse'
            ? 'waiter.notes.reservations.printSentCaisse'
            : 'waiter.notes.reservations.printSentCloud',
          { name: reservation.customerName },
        ),
      };
    } catch (err) {
      const code = (err as { data?: { code?: string } })?.data?.code;
      const key =
        code === 'NO_PRINT_DESTINATION'
          ? 'waiter.notes.reservations.printNoDestination'
          : code === 'NOT_PRINTABLE'
            ? 'waiter.notes.reservations.printNotPrintable'
            : code === 'HUB_UNAVAILABLE'
              ? 'waiter.notes.reservations.errorUnavailable'
              : 'waiter.notes.reservations.printFailed';
      return { ok: false, message: t(key) };
    }
  };

  // SC-1281 — le client rappelle. Déplacée à un autre jour, la réservation
  // quitte la liste du jour : le toast dit où elle est partie.
  const handleUpdateReservation = async (
    draft: NewReservationDraft,
    reservedForIso: string,
  ): Promise<string | null> => {
    if (!token || !editingResa) return t('waiter.notes.reservations.errorUnavailable');
    try {
      const updated = await updateReservation({
        token,
        reservationUuid: editingResa.uuid,
        customerName: draft.customerName.trim(),
        partySize: draft.partySize,
        reservedFor: reservedForIso,
        customerPhone: draft.customerPhone.trim(),
        notes: draft.notes.trim(),
      }).unwrap();
      const at = Date.parse(updated.reservedFor);
      showToast?.(
        toDateInput(at) === toDateInput(Date.now())
          ? t('waiter.notes.reservations.toastEdited', { name: updated.customerName })
          : t('waiter.notes.reservations.toastMoved', {
              name: updated.customerName,
              date: new Date(at).toLocaleDateString(i18n.language, {
                weekday: 'short',
                day: 'numeric',
                month: 'short',
              }),
              time: formatTicketTime(updated.reservedFor),
            }),
      );
      return null;
    } catch (err) {
      const code = (err as { data?: { code?: string } })?.data?.code;
      if (code === 'NOT_EDITABLE') return t('waiter.notes.reservations.errorNotEditable');
      if (code === 'PAST_DATE') return t('waiter.notes.reservations.errorPastDate');
      if (code === 'RESERVATION_DISABLED') return t('waiter.notes.reservations.errorModuleClosed');
      if (code === 'HUB_UNAVAILABLE') return t('waiter.notes.reservations.errorUnavailable');
      return t('waiter.notes.reservations.errorEdit');
    }
  };

  const handleSeatTable = (reservation: WaiterReservation, table: WaiterTable) => {
    setSeatFor(reservation);
    setOpenTarget(table);
  };

  const handleTableOpened = (ticket: CreatedTicket) => {
    setOpenTarget(null);
    // La table est ouverte : la reservation est honoree. L'appel part apres
    // la navigation — s'il echoue, le toast le dit, la table reste ouverte.
    const reservation = seatFor;
    setSeatFor(null);
    if (reservation) {
      void applyReservationStatus(reservation, 'COMPLETED').then((error) => {
        if (error !== null) {
          showToast?.(t('waiter.notes.reservations.seatedNotMarked', { name: reservation.customerName }));
        }
      });
    }
    dispatch(setTicket(ticket));
    navigate(`/${restaurantUuid}/serveur/ticket/${ticket.sessionUuid}`);
  };

  const emporterCards = useMemo<NoteCard[]>(() => {
    return [...(takeawayNotes ?? [])]
      .sort((a, b) => compareNoteLabels(a.label, b.label))
      .map((note) => ({
        sessionUuid: note.uuid,
        kind: 'takeaway',
        label: note.label,
        covers: null,
        openedAt: note.openedAt,
        runningTotal: note.runningTotal,
        pendingValidation: note.pendingValidation,
      }));
  }, [takeawayNotes]);

  // §2.3 D5-D7 — Emporter's own filter (Tout/Comptoir/En ligne, default
  // Tout) plus the sorted « En ligne » rows and the tapped order's detail
  // modal target.
  const [emporterFilter, setEmporterFilter] = useState<EmporterFilterKey>('all');
  // SC-1274 — Emporter | Livraison, pour un restaurant qui livre seulement.
  // Pas gardee d'une ouverture a l'autre : Emporter est le cas courant.
  const deliveryEnabled = useWaiterDeliveryEnabled(restaurantUuid);
  const [emporterKind, setEmporterKind] = useState<EmporterKind>(() =>
    readKindFromSearch(window.location.search),
  );
  const showDelivery = deliveryEnabled && emporterKind === 'livraison';
  // review F3#7a — `?? []` moved inside the memo callback instead of a
  // standalone `const onlineOrders = onlineOrdersData ?? []` above it (same
  // fresh-array-every-render cause as `allTables`).
  // SC-961 — most recently received first: the order a customer just placed
  // is the one the waiter is looking for, and it used to sit at the bottom.
  const sortedOnlineOrders = useMemo(
    () => sortOnlineOrdersRecentFirst(onlineOrdersData ?? []),
    [onlineOrdersData],
  );
  // review F3#7b — the soonest pickup still ahead of now, scanned rather than
  // read off the first row: the list no longer runs in scheduledAt order.
  // SC-1274 — un restaurant qui livre sort ses livraisons de « En ligne » ;
  // sans la vue Livraison, « En ligne » les garde, exactement comme avant.
  const takeawayOnlineOrders = useMemo(
    () => (deliveryEnabled ? sortedOnlineOrders.filter((o) => o.type !== 'delivery') : sortedOnlineOrders),
    [deliveryEnabled, sortedOnlineOrders],
  );
  const deliveryOrders = useMemo(
    () => (deliveryEnabled ? sortedOnlineOrders.filter((o) => o.type === 'delivery') : []),
    [deliveryEnabled, sortedOnlineOrders],
  );
  const nextPickupOrder = findNextPickup(takeawayOnlineOrders);
  const emporterAllEmpty =
    emporterCards.length === 0 && takeawayOnlineOrders.length === 0 && !onlineOrdersError;
  const [onlineOrderTarget, setOnlineOrderTarget] = useState<WaiterOnlineOrder | null>(null);

  // Pastille de chaque onglet : ce qui est EN COURS, comme celle des resas.
  // Sur place — une table libre n'attend rien, une table payee est terminee ;
  // toutes salles confondues, le filtre de salle ne touche pas l'onglet ;
  // une tablee reunie (SC-976) compte pour une.
  // Emporter — les notes comptoir sont deja les ouvertes, et `online-orders/`
  // ne renvoie que received|preparing|ready (ni remise, ni annulee).
  const tabCounts: Record<NotesTab, number> = {
    surplace: oneRowPerSession(allTables).filter((tb) => {
      const state = deriveTableState(tb);
      return state === 'seated' || state === 'ordered';
    }).length,
    emporter: emporterCards.length + sortedOnlineOrders.length,
    reservations: visibleResas.length,
  };

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const handleCreated = (ticket: CreatedTicket) => {
    setCreateModalOpen(false);
    dispatch(setTicket(ticket));
    navigate(`/${restaurantUuid}/serveur/ticket/${ticket.sessionUuid}`);
  };

  // SC-761 — « changer de serveur » asks WHO first, then the code. Tapping the
  // identity chip used to lock the device on the spot and drop the name, so
  // the waiter met a bare PIN pad with no way back and had to re-pick a name
  // afterwards. The roster now lives in a sheet; the re-PIN (spec §2.3, the
  // only endpoint that can rebind a name) happens after the pick and can be
  // cancelled.
  const roster = waiterNames ?? [];
  const [switchSheetOpen, setSwitchSheetOpen] = useState(false);
  // SC-1082 — « Signaler un bug » ouvre sur la fermeture de la feuille
  // d'identité, jamais par-dessus (deux feuilles empilées, un seul voile
  // pour deux modales : `BugReportSheet` en tient une à lui seul).
  const [bugReportOpen, setBugReportOpen] = useState(false);
  // Fidélité — « Scanner » : le client montre sa carte ou un avantage, le
  // serveur le valide à table. La touche n'existe que si le restaurant a un
  // programme en marche : sans programme, elle ne pourrait que répondre
  // « désactivé ». SC-1643 — elle est dans la rangée d'onglets, juste avant
  // la pastille du serveur, AUSSI quand il y a une liste de serveurs : cachée
  // dans la feuille « Serveur en service », il fallait deux touches pour
  // scanner, à chaque client. La feuille garde sa ligne en tête (les habitués
  // la connaissent) ; comme « Signaler un bug », elle ouvre sur la fermeture
  // de la feuille. Le prix en largeur est compté sur la pastille (seuils du
  // prénom) et sur les onglets, voir plus bas.
  const [loyaltyScanOpen, setLoyaltyScanOpen] = useState(false);
  const { data: loyaltyProgram } = useWaiterLoyaltyProgramQuery(restaurantUuid);
  const loyaltyEnabled = loyaltyProgram?.enabled === true;

  // Long-press / ⋮ « Transférer la table » — existing move flow (SC-383),
  // reused as-is and kept minimal (no dedicated tests in this slice; the
  // underlying mutation contract is already proven by TablesOverview.test.tsx).
  //
  // SC-976 — keyed by the TABLE's own uuid, not its session's: a member row
  // shares its anchor's session uuid, so keying on the session would always
  // resolve back to whichever row `allTables` lists first, and « Séparer la
  // table 6 » needs to know it was the 6's ⋮ that was tapped, not the 5's.
  const [transferSourceTableUuid, setTransferSourceTableUuid] = useState<string | null>(null);
  // Review fix-T3-c P2#1 (Codex) — the SESSION that was live on that table
  // when the sheet opened. `transferSourceTable` below re-resolves off the
  // live `allTables` on every `tables/` poll, so if ANOTHER device closes
  // that session (or transfers it away) and a brand-new party opens the
  // SAME physical table while this sheet is still up, the sheet would
  // silently start acting on the NEW session with the OLD draft (covers,
  // pending detach target…). The watcher effect below closes the
  // sheet the moment the live session uuid stops matching this one.
  const [transferSourceSessionUuid, setTransferSourceSessionUuid] = useState<string | null>(null);
  const [transferError, setTransferError] = useState<string | null>(null);
  // DF-0170 — the move used to fire on the destination tap. Two touches (a
  // phone rubbing in a pocket) were enough to reassign a whole table: on
  // 2026-09-06 table 5 became table 19 mid-service, kitchen slip « TABLE 5 »
  // against a bill saying « table 19 ». The destination tap now only *arms*
  // the move; a confirmation naming BOTH tables and the amount at stake is
  // the only thing that calls the network.
  //
  // SC-976 regroup (2026-09-16) — the sheet's tabs: « Transférer » (the
  // tablée's tables BECOME the picked set, up to 8, free or occupied — a
  // 5-table tablée used to be capped at 3 picks) / « Réunir » (tables are
  // added; an occupied one brings its whole note and bill) / « Séparer ».
  // `regroupPicked` is the grid's selection (uuids, tap order, first = new
  // anchor in move); `regroupPending` is the armed request the confirmation
  // shows — `null` while still picking.
  const [regroupTab, setRegroupTab] = useState<RegroupMode | 'detach'>('move');
  const [regroupPicked, setRegroupPicked] = useState<string[]>([]);
  // SC-1007 — a pick that lands on another note always merges its bill in:
  // « Écraser » (cancel that note) is gone.
  const [regroupPending, setRegroupPending] = useState<{
    mode: RegroupMode;
    picked: string[];
  } | null>(null);
  // …and the request is single-flight: prod logged the two POSTs of that
  // accident inside the same second (200 then 409), because nothing disabled
  // the target while the first one was in the air.
  const [transferBusy, setTransferBusy] = useState(false);
  const [regroupSession] = useRegroupSessionMutation();
  const transferSourceTable = allTables.find((tb) => tb.uuid === transferSourceTableUuid) ?? null;
  const transferSourceIsMerged = transferSourceTable?.session?.isMerged === true;
  const transferSourceIsMember = transferSourceIsMerged && transferSourceTable?.role === 'member';
  // D3 — l'ancre est toujours en tête de `tableUuids`/`tableNumbers` ; les
  // rattachées sont les entrées suivantes, dans l'ordre où elles ont été
  // rattachées.
  const transferSourceMembers = (transferSourceTable?.session?.tableUuids ?? [])
    .map((uuid, i) => ({ uuid, number: transferSourceTable?.session?.tableNumbers?.[i] ?? '' }))
    .slice(1);
  const transferSourceAnchorNumber = transferSourceTable?.session?.tableNumbers?.[0] ?? '';
  // D2 — le titre de la feuille ⋮ affiche l'étiquette de tablée quand il y en
  // a une (jamais recomposée : c'est déjà la chaîne du serveur), sinon le
  // numéro simple de la table tapée, identique à avant (D12).
  const sheetLabel = transferSourceTable?.session?.tableLabel ?? transferSourceTable?.number ?? '';
  const tablesByUuid = useMemo(() => new Map(allTables.map((tb) => [tb.uuid, tb])), [allTables]);
  const labelOf = (uuids: string[]) => uuids.map((uuid) => tablesByUuid.get(uuid)?.number ?? '?').join('+');
  const regroupTabs: Array<RegroupMode | 'detach'> =
    transferSourceIsMerged && transferSourceMembers.length > 0 ? ['move', 'merge', 'detach'] : ['move', 'merge'];
  // A tablée split by another device while the sheet is open loses its
  // « Séparer » tab: fall back to the first one rather than an empty panel.
  const activeRegroupTab = regroupTabs.includes(regroupTab) ? regroupTab : 'move';
  const regroupMode: RegroupMode = activeRegroupTab === 'merge' ? 'merge' : 'move';
  const regroupResultUuids = transferSourceTable
    ? regroupResult(regroupMode, transferSourceTable, regroupPicked, tablesByUuid)
    : [];
  // What the submit button names: the whole result in move, only the added
  // tables in merge.
  const regroupPickedLabel = labelOf(
    regroupMode === 'merge'
      ? regroupResultUuids.filter((uuid) => !(transferSourceTable?.session?.tableUuids ?? []).includes(uuid))
      : regroupResultUuids,
  );
  // Review P2#7 pattern: a picked table that vanished from the live poll
  // leaves the selection. Bails out on the same array when nothing changed.
  useEffect(() => {
    setRegroupPicked((prev) => {
      const next = prev.filter((uuid) => tablesByUuid.has(uuid));
      return next.length === prev.length ? prev : next;
    });
  }, [tablesByUuid]);
  const closeTransfer = () => {
    setTransferSourceTableUuid(null);
    setTransferSourceSessionUuid(null);
    setRegroupPicked([]);
    setRegroupPending(null);
    setRegroupTab('move');
    setTransferBusy(false);
    // Review finding P0#1 — the success path of confirmDetach calls
    // closeTransfer() instead of its own catch's setDetachBusy(false):
    // without resetting it here too, the flag stayed true forever after the
    // FIRST successful Séparer, and the guard at the top of confirmDetach
    // (`if (... || detachBusy) return`) silently no-op'd every later
    // attempt, its confirm button stuck on « Séparation en cours… ».
    setDetachError(null);
    setDetachPendingTarget(null);
    setDetachBusy(false);
  };
  // Review fix-T3-c P2#1 (Codex) — closes the sheet out from under a stale
  // draft the moment the table's LIVE session stops matching the one it was
  // opened for: cleared elsewhere, moved away, or the table went free and a
  // new party opened it before the next `tables/` poll caught up. Every
  // mutation below (`saveCovers`/`confirmDetach`/`confirmRegroup`) still
  // reads `transferSourceTable.session.uuid` at the
  // moment it fires, but a wide-open sheet showing yesterday's covers/total
  // while quietly pointed at someone else's session is a worse failure mode
  // than just closing it.
  useEffect(() => {
    if (!transferSourceTableUuid) return;
    const liveSessionUuid = transferSourceTable?.session?.uuid ?? null;
    if (liveSessionUuid !== transferSourceSessionUuid) closeTransfer();
  }, [transferSourceTable, transferSourceTableUuid, transferSourceSessionUuid, closeTransfer]);
  // SC-958 — the ⋮ sheet also corrects the covers count (same place as the
  // 转台, as the ticket asks). Seeded from the tile when the sheet opens; the
  // ceiling is the table's `maxCovers`, the one the open prompt already uses.
  const [coversDraft, setCoversDraft] = useState(1);
  const [coversError, setCoversError] = useState<string | null>(null);
  const [updateSessionCovers, { isLoading: coversBusy }] = useUpdateSessionCoversMutation();
  const activeTicket = useAppSelector(selectActiveTicket);
  const coversMax = Math.max(1, transferSourceTable?.maxCovers ?? 1);
  const saveCovers = async () => {
    const source = transferSourceTable;
    if (!source || !source.session || coversBusy) return;
    setCoversError(null);
    try {
      await updateSessionCovers({ sessionUuid: source.session.uuid, covers: coversDraft, token }).unwrap();
      // TicketPage skips its re-join when the ticket is already the active one,
      // so its header would keep the old count without this.
      if (activeTicket?.sessionUuid === source.session.uuid) {
        dispatch(setTicket({ ...activeTicket, covers: coversDraft }));
      }
      closeTransfer();
      void refetchTables();
    } catch (err: unknown) {
      const apiErr = err as { data?: { code?: string; max_covers?: number } };
      setCoversError(
        apiErr.data?.code === 'covers_exceeds_capacity'
          ? t('waiter.notes.errors.coversExceedsCapacity', { n: apiErr.data.max_covers ?? coversMax })
          : t('waiter.notes.errors.generic'),
      );
    }
  };
  // SC-976 — design §3.5/§5.1 « Séparer » : arme sur le tap d'une rattachée
  // (depuis une tuile rattachée, c'est la table elle-même ; depuis l'ancre
  // d'une tablée, c'est l'une des puces de la liste à cocher).
  const [detachTable] = useDetachTableMutation();
  const [detachPendingTarget, setDetachPendingTarget] = useState<{ uuid: string; number: string | number } | null>(
    null,
  );
  const [detachBusy, setDetachBusy] = useState(false);
  const [detachError, setDetachError] = useState<string | null>(null);
  const confirmDetach = async () => {
    const source = transferSourceTable;
    const target = detachPendingTarget;
    if (!source || !source.session || !target || detachBusy) return;
    setDetachError(null);
    setDetachBusy(true);
    try {
      const result = await detachTable({ sessionUuid: source.session.uuid, tableUuid: target.uuid, token }).unwrap();
      // E-34 — même patron que l'attache : l'en-tête du ticket ouvert suit.
      if (activeTicket?.sessionUuid === source.session.uuid) {
        dispatch(
          setTicket({ ...activeTicket, label: result.tableLabel ?? activeTicket.label, isMerged: result.isMerged }),
        );
      }
      closeTransfer();
      void refetchTables();
    } catch {
      setDetachError(t('waiter.overview.detachGenericError'));
      setDetachPendingTarget(null);
      setDetachBusy(false);
      void refetchTables();
    }
  };
  const confirmRegroup = async () => {
    const source = transferSourceTable;
    const pending = regroupPending;
    if (!source || !source.session || !pending || transferBusy) return;
    setTransferError(null);
    setTransferBusy(true);
    try {
      const result = await regroupSession({
        sessionUuid: source.session.uuid,
        mode: pending.mode,
        tableUuids: pending.picked,
        // Only sent when another note is involved.
        onOccupied: absorbedSessions(source, pending.picked, tablesByUuid).length > 0 ? 'merge' : undefined,
        token,
      }).unwrap();
      // E-34 — the open ticket's header follows the new label.
      if (activeTicket?.sessionUuid === source.session.uuid) {
        dispatch(
          setTicket({
            ...activeTicket,
            label: result.tableLabel ?? activeTicket.label,
            isMerged: result.isMerged,
            covers: result.covers ?? activeTicket.covers,
          }),
        );
      }
      closeTransfer();
      void refetchTables();
    } catch (err: unknown) {
      const apiErr = err as { data?: { code?: string; table_number?: string | number; max?: number } };
      const number = apiErr.data?.table_number ?? '';
      const code = apiErr.data?.code;
      setTransferError(
        code === 'TOO_MANY_TABLES'
          ? t('waiter.overview.attachTooManyError', { max: apiErr.data?.max ?? MAX_TABLES_PER_SESSION })
          : code === 'GUEST_PAYMENT_IN_FLIGHT'
            ? t('waiter.overview.regroupPaymentError', { number })
            : code === 'ORDER_AT_CAISSE'
              ? t('waiter.overview.regroupCaisseError', { number })
              : code === 'ROUND_TO_VALIDATE'
                ? t('waiter.overview.regroupRoundError', { number })
                : code === 'BUFFET_NOT_MERGEABLE'
                  ? t('waiter.overview.regroupBuffetError')
                  : code === 'STALE'
                    ? t('waiter.overview.regroupStaleError')
                    : t('waiter.overview.transferGenericError'),
      );
      // Back to the picker (selection kept, minus whatever the refetch shows
      // as gone) so the waiter can fix the choice.
      setRegroupPending(null);
      setTransferBusy(false);
      void refetchTables();
    }
  };

  // SC-966 — la 3e tuile n'apparait que si le module reservation est ouvert.
  // Tant qu'elle n'est pas la, une preference enregistree sur « Résas »
  // retombe sur Sur place sans etre effacee (le module peut revenir).
  const tabs: NotesTab[] = reservationsEnabled ? [...BASE_TABS, 'reservations'] : BASE_TABS;
  const activeTab: NotesTab = tabs.includes(tab) ? tab : 'surplace';
  useLayoutEffect(() => {
    const grid = tableGridRef.current;
    if (!grid) return;
    const measure = () => {
      const tracks = getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean).length;
      if (tracks > 0) setGridColumns(tracks);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(grid);
    return () => observer.disconnect();
  }, [activeTab]);
  const tabSwipe = useTabSwipe(tabs, activeTab, setTab);
  // Les deux onglets ou l'on CREE quelque chose portent le meme pied de page.
  // SC-1274 — pas sur la vue Livraison : « Nouvelle note » y ferait croire
  // qu'on cree une livraison, alors qu'elle ouvre une note comptoir.
  const hasFooter = (activeTab === 'emporter' && !showDelivery) || activeTab === 'reservations';

  // ARIA tabs pattern — ←/→ move between the tabs, Home/End jump to the
  // ends. The tab strip is one tab stop (`tabIndex` follows selection).
  const handleTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const index = tabs.indexOf(activeTab);
    let next: NotesTab | null = null;
    if (event.key === 'ArrowRight') next = tabs[(index + 1) % tabs.length];
    else if (event.key === 'ArrowLeft') next = tabs[(index - 1 + tabs.length) % tabs.length];
    else if (event.key === 'Home') next = tabs[0];
    else if (event.key === 'End') next = tabs[tabs.length - 1];
    if (!next) return;
    event.preventDefault();
    setTab(next);
    document.getElementById(`waiter-tab-${next}`)?.focus();
  };

  return (
    <div
      className="waiter-root bg-cream"
      data-testid="notes-page"
      onContextMenu={(event) => event.preventDefault()}
    >
      {/* The header row is now the safe-area inset plus the offline banner
          and nothing else: the « Carnet » title and the identity chip used to
          cost a 48px row of their own above a 56px tab row. The title said
          what the two tabs already say, so it is gone and the chip moved down
          into the tab row — ~56px handed back to the table grid, half a tile
          row on a 360px phone. */}
      <header data-testid="notes-header" className="bg-paper waiter-safe-top">
        {isOffline && (
          <p
            data-testid="notes-offline-banner"
            role="alert"
            className="text-center font-sans text-sm font-bold text-white py-1.5 px-3"
            style={{ backgroundColor: 'var(--w-danger)' }}
          >
            {t('waiter.notes.offline')}
          </p>
        )}
        {/* SC-906 — meme emplacement que la banniere ci-dessus, et meme
            economie : la bande ne coute ses 40px que tant que le carnet n'est
            pas installe et que le serveur ne l'a pas refermee. */}
        <InstallAppPrompt variant="banner" />
      </header>

      {/* The identity chip is a SIBLING of the tablist, never inside it: a
          `role="tablist"` may only contain tabs. SC-979 — la marque du
          restaurant l'est aussi, et pour la même raison ; elle tient dans les
          48px de cette rangée et ne coûte donc aucune ligne au carnet. */}
      <div className="flex items-center bg-paper border-b-2 border-hairline">
        {restaurantName && (
          <RestaurantMark
            name={restaurantName}
            logoUrl={restaurantLogoUrl}
            logoInverseUrl={restaurantLogoInverseUrl}
          />
        )}
        <div
          data-testid="waiter-tabs"
          role="tablist"
          aria-label={t('waiter.notes.title')}
          // Trois colonnes rigides donnaient a « Resas » 62px de marge
          // inutilisee pendant que « Sur place » et « Emporter » se coupaient
          // a cote (mesure a 390px). En `basis-auto`, chaque onglet prend la
          // largeur de son libelle puis se partage le reste a egalite : le
          // court rend ce qu'il n'utilise pas, et plus personne ne se coupe
          // avant que la rangee ne soit vraiment pleine.
          className="flex flex-1 min-w-0 self-stretch"
        >
          {tabs.map((key) => {
            const selected = activeTab === key;
            return (
              <button
                key={key}
                type="button"
                id={`waiter-tab-${key}`}
                data-testid={`waiter-tab-${key}`}
                role="tab"
                aria-selected={selected}
                aria-controls="waiter-tabpanel"
                tabIndex={selected ? 0 : -1}
                onClick={() => setTab(key)}
                onKeyDown={handleTabKeyDown}
                // `px-1` n'est pas de la respiration : c'est la seule
                // gouttiere entre deux onglets. Sans elle la pastille bleue
                // d'un onglet finissait a 2px du libelle du voisin et les deux
                // se lisaient comme un seul mot, « 3Empor… » (revue visuelle).
                className={`h-12 px-1 grow shrink basis-auto min-w-0 leading-tight border-b-4 font-sans ${
                  // Trois onglets a 320px : 18px ne tient pas (« Sur place »
                  // seul mesure ~80px pour 86 disponibles, et « テイクアウト »
                  // davantage). 16px garde les trois labels sur une ligne, et
                  // la hauteur de la rangee ne bouge pas.
                  tabs.length === 3 ? 'text-base' : 'text-[18px]'
                } ${
                  // The selected tab wears the pane's own background so the
                  // two read as one surface (`--ash` and `--cream` are the same
                  // value under any restaurant theme — applyTheme sets both
                  // from pageBackground — so this is the list pane's colour).
                  selected
                    ? 'font-bold text-ink border-ink bg-cream'
                    : 'font-semibold text-ink-mute border-transparent'
                }`}
              >
                {/* `max-w-full` is what makes the label's `truncate` bite: an
                    `inline-flex` sizes to its content, so without it the label
                    overflowed its 1fr grid cell and PAINTED OVER the next tab's
                    text (mesuré 2026-09-13 : fr/en/ja/ar dès 320px, et jusqu'à
                    412px en ar — l'ellipse n'apparaissait jamais). */}
                <span className="inline-flex items-center justify-center gap-0.5 min-w-0 max-w-full">
                  {/* Plancher : le partage a egalite prend aussi sur l'onglet
                      court, et sous ~3 signes une etiquette ne veut plus rien
                      dire — « الحجوزات » coupe a « الحج » est un AUTRE mot
                      arabe, pas une abreviation du sien. 2,5em ne mord sur
                      aucun libelle d'aujourd'hui (le plus court, « Résas »,
                      mesure 44px pour 40 de plancher) : c'est un garde-fou,
                      pas une repartition. */}
                  <span className="truncate min-w-[2.5em]">{t(`waiter.notes.tabs.${key}`)}</span>
                  {tabCounts[key] > 0 && (
                    <span
                      data-testid={`${key}-tab-count`}
                      className="shrink-0 min-w-[20px] h-[20px] px-1 rounded-full text-white font-sans text-[13px] font-bold flex items-center justify-center"
                      style={{ backgroundColor: 'var(--w-info)' }}
                    >
                      {tabCounts[key]}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
        {/* Installe en appli, le carnet n'a ni bouton « recharger » de
            navigateur ni tirer-pour-actualiser (`useWaiterDocumentLock` le
            coupe) : c'est le seul moyen de repartir d'une page fraiche, et de
            prendre la derniere version mise en ligne. Grise hors reseau : un
            rechargement sans connexion ne ramenerait pas le carnet. */}
        <button
          type="button"
          data-testid="notes-reload"
          aria-label={t('waiter.notes.refresh')}
          title={t('waiter.notes.refresh')}
          disabled={!isOnline}
          onClick={() => window.location.reload()}
          className="shrink-0 w-9 h-11 flex items-center justify-center rounded-btn-sm text-ink disabled:opacity-40"
        >
          <svg
            viewBox="0 0 24 24"
            className="w-6 h-6"
            aria-hidden="true"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 12a9 9 0 1 1-2.64-6.36" />
            <path d="M21 3v6h-6" />
          </svg>
        </button>
        {/* SC-1643 — la touche « Scanner » juste avant la pastille, large de
            36px comme le bouton recharger (44px de haut), sans marge propre :
            le `ms-1.5` de la pastille les separe. Sous 400px, la pastille
            rend aussi son ▾ (voir plus bas). A 360px, trois onglets, logo de
            40px : marque 52 + recharger 36 + touche 36 + pastille 48 et ses
            marges 12 = 184px fixes, il reste 176px aux onglets (200 sans la
            touche) pour 3 x 48px de plancher (2,5em de libelle + `px-1`) =
            144 : la rangee reste sur une ligne et aucun libelle ne deborde
            sur son voisin. Mesure (Fira Sans) : les trois libelles fr sont
            entiers des 388px (364 sans la touche), donc a 390. Sans liste de
            serveurs, elle termine la rangee, a sa taille d'avant. */}
        {loyaltyEnabled && (
          <button
            type="button"
            data-testid="loyalty-scan-key"
            aria-haspopup="dialog"
            aria-label={t('waiter.loyaltyScan.open')}
            title={t('waiter.loyaltyScan.open')}
            onClick={() => setLoyaltyScanOpen(true)}
            className={`shrink-0 ${
              roster.length > 0 ? 'w-9' : 'w-11 me-1.5'
            } h-11 flex items-center justify-center rounded-btn-sm text-ink`}
          >
            <ScanIcon />
          </button>
        )}
        {roster.length > 0 && (
          <button
            type="button"
            data-testid="change-waiter"
            aria-haspopup="dialog"
            aria-expanded={switchSheetOpen}
            aria-label={t('waiter.switchSheet.trigger', { name: waiterName ?? '' })}
            onClick={() => setSwitchSheetOpen(true)}
            // Largeur fluide : le prenom prend ce que la rangee peut lui
            // preter (30 % au plus), plafonne a 12rem. Un pas fixe le coupait
            // a 6 signes meme quand 57px de rangee restaient vides.
            className="shrink-0 h-11 max-w-[min(12rem,30vw)] mx-1.5 flex items-center gap-1.5 rounded-btn-sm border-2 border-ink bg-paper ps-1 pe-2 font-sans text-base font-semibold text-ink"
          >
            <span
              aria-hidden="true"
              className="w-8 h-8 shrink-0 rounded-full bg-ink text-paper flex items-center justify-center text-sm font-bold"
            >
              {(waiterName ?? '').trim().slice(0, 1).toUpperCase()}
            </span>
            {/* Le nom cede la place aux libelles d'onglets tant que la rangee
                est etroite : l'initiale du rond suffit a dire qui est en poste,
                et la marque du restaurant, elle, n'a pas de repli. Seuil porte
                de 400 a 600px dans le cas a 3 onglets : c'est la largeur a
                partir de laquelle la rangee porte les trois libelles ET le
                prenom. En dessous, les libelles passent avant — on les lit a
                chaque coup d'oeil, le prenom une fois par service.
                SC-1643 — avec la touche « Scanner » (36px), mesure (Fira Sans,
                prenom de 15 signes plafonne a 30vw) : libelles entiers + prenom
                des 486 (fr), 506 (en), 586 (ar) a 3 onglets — 600 tient
                toujours ; des 436 (fr, ja) a 2 onglets — le seuil y passe de
                400 a 440px. */}
            <span
              // `dir="auto"`: en arabe, un prenom latin tronque par la fin de
              // la ligne RTL perdait son DEBUT — « Christophe » s'affichait
              // « …ophe », ce qui n'identifie personne. La direction se prend
              // desormais sur le prenom lui-meme, pas sur la page.
              dir="auto"
              className={
                tabs.length === 3
                  ? 'truncate hidden min-[600px]:inline'
                  : loyaltyEnabled
                    ? 'truncate hidden min-[440px]:inline'
                    : 'truncate hidden min-[400px]:inline'
              }
            >
              {waiterName}
            </span>
            {/* SC-1643 — avec la touche « Scanner » a cote, le ▾ cede ses 12px
                aux onglets sous 400px : le cadre de la pastille dit deja
                « bouton ». */}
            <span
              aria-hidden="true"
              className={loyaltyEnabled ? 'text-xs hidden min-[400px]:inline' : 'text-xs'}
            >
              ▾
            </span>
          </button>
        )}
      </div>

      <div
        id="waiter-tabpanel"
        role="tabpanel"
        aria-labelledby={`waiter-tab-${activeTab}`}
        // SC-972 — glisser change d'onglet. Le geste est juge au relachement :
        // rien ne bouge sous le doigt, donc rien n'est rendu pendant le
        // deplacement, et les barres de pastilles gardent le leur.
        onTouchStart={tabSwipe.onTouchStart}
        onTouchEnd={tabSwipe.onTouchEnd}
        // Sur place has no footer any more, so the last tile row would sit
        // under the iOS home indicator without picking up the inset here.
        // L'inset bas appartient au pied de page quand il y en a un, sinon au
        // panneau qui defile — sinon on le compte deux fois.
        className={`waiter-scroll min-h-0${hasFooter ? '' : ' waiter-safe-bottom'}`}
      >
        {activeTab === 'reservations' ? (
          <ReservationsPanel
            reservations={resaDay ? otherDay?.reservations ?? [] : resasOfTheDay}
            graceMinutes={resasGraceMinutes}
            nowMs={nowMs}
            loadError={!!(resaDay ? otherDayError : reservationsError)}
            updatedAtMs={resaDay ? otherDayStamp : reservationsStamp}
            onRefresh={() => void (resaDay ? refetchOtherDay() : refetchReservations())}
            day={resaDay ? otherDay?.date ?? resaDay : reservationsDay?.date}
            today={reservationsDay?.date}
            onChangeDay={setResaDay}
            token={token}
            loading={!!resaDay && !otherDay && !otherDayError}
            tables={allTables}
            onSetStatus={handleReservationStatus}
            onSeat={handleSeatTable}
            onEdit={setEditingResa}
            onPrint={handlePrintReservation}
          />
        ) : activeTab === 'surplace' ? (
          <>
            <div className="sticky top-0 z-10 bg-cream">
              <SalleBar
                areas={areas ?? []}
                tables={allTables}
                selected={effectiveSalleKey}
                onSelect={setSalleKey}
              />
              <TableStateFilter tables={salleTables} active={stateFilter} onToggle={toggleStateFilter} onShowAll={() => setStateFilter(null)} />
            </div>
            <div
              ref={tableGridRef}
              data-testid="table-grid"
              // 6px de gouttière : la pastille rouge déborde de 6px (-top-1.5),
              // moins et elle mord la tuile voisine. Plancher 100px : à 320px la
              // grille reste à 2 grandes tuiles au lieu de 3 de 97px.
              className="grid grid-cols-[repeat(auto-fill,minmax(100px,1fr))] gap-1.5 p-2 content-start"
            >
              {tileChunks.map((chunk, index) => {
                const renderTile = (tb: WaiterTable, members: WaiterTable[] = [], span = 1) => (
                  <TableTile
                    key={tb.uuid}
                    table={tb}
                    members={members}
                    columnSpan={span}
                    unsentQty={tb.session ? readUnsentWaiterQty(tb.session.uuid) : 0}
                    onOpenFree={handleTileTap}
                    onNavigateOccupied={handleTileTap}
                    onOpenPending={handlePendingTap}
                    onTransfer={(target) => {
                      if (!target.session) return;
                      setTransferError(null);
                      setRegroupPicked([]);
                      setRegroupPending(null);
                      setRegroupTab('move');
                      setCoversError(null);
                      setCoversDraft(target.session.covers);
                      setDetachError(null);
                      setDetachPendingTarget(null);
                      // SC-976 — le uuid de la TABLE tapée, pas de sa session
                      // (une rattachée partage la session de son ancre) :
                      // c'est ce qui distingue « Séparer la 6 » de « Séparer…
                      // » ouvert depuis la 5.
                      setTransferSourceTableUuid(target.uuid);
                      setTransferSourceSessionUuid(target.session.uuid);
                    }}
                  />
                );
                return renderTile(chunk[0], chunk.slice(1), chunkSpans[index]);
              })}
            </div>
            {/* review F1F2#8 — no empty state existed for Sur place: zero
                tables, an empty salle or a state-filter chip matching
                nothing rendered a bare grid with no way to tell why. */}
            {sortedTiles.length === 0 && (
              <p
                data-testid="surplace-empty"
                className="px-3 pb-4 font-sans text-base text-ink-mute text-center"
              >
                {t('waiter.notes.empty.surplace')}
              </p>
            )}
          </>
        ) : (
          <>
            {/* SC-1274 — sans livraison, rien ici ne change : pas de selecteur,
                et la page vide reste la meme, seule dans le panneau. */}
            {(deliveryEnabled || !emporterAllEmpty) && (
              <div className="sticky top-0 z-10 bg-cream">
                {deliveryEnabled && (
                  <EmporterKindSwitch
                    emporterCount={emporterCards.length + takeawayOnlineOrders.length}
                    deliveryCount={deliveryOrders.length}
                    active={emporterKind}
                    onSelect={setEmporterKind}
                    standalone={showDelivery || emporterAllEmpty}
                  />
                )}
                {!showDelivery && !emporterAllEmpty && (
                  <EmporterSourceFilter
                    counterCount={emporterCards.length}
                    onlineCount={takeawayOnlineOrders.length}
                    active={emporterFilter}
                    onSelect={setEmporterFilter}
                  />
                )}
              </div>
            )}

            {showDelivery ? (
              <div data-testid="delivery-section" className="flex flex-col">
                <h2 className="px-3 pt-3 pb-1 font-sans text-base font-bold text-ink">
                  {t('waiter.notes.delivery.sectionTitle', { n: deliveryOrders.length })}
                </h2>
                {onlineOrdersError && (
                  <p
                    data-testid="delivery-section-error"
                    role="alert"
                    className="px-3 pb-2 font-sans text-sm font-semibold"
                    style={{ color: 'var(--w-danger)' }}
                  >
                    {t('waiter.notes.online.loadError')}
                  </p>
                )}
                {deliveryOrders.length === 0 ? (
                  <p className="px-3 pb-4 font-sans text-base text-ink-mute">{t('waiter.notes.delivery.empty')}</p>
                ) : (
                  <div className="flex flex-col">
                    {deliveryOrders.map((order) => (
                      <OnlineOrderRow key={order.uuid} order={order} onTap={setOnlineOrderTarget} />
                    ))}
                  </div>
                )}
              </div>
            ) : emporterAllEmpty ? (
              <div
                data-testid="emporter-empty"
                className={`${deliveryEnabled ? 'py-16' : 'h-full'} flex flex-col items-center justify-center gap-1 px-4 text-center`}
              >
                <p className="font-sans text-[18px] font-medium text-ink-mute">
                  {/* Des livraisons en cours : « aucune commande » mentirait
                      a cote de leur pastille. */}
                  {t(deliveryOrders.length > 0 ? 'waiter.notes.kind.emptyEmporter' : 'waiter.notes.empty.title')}
                </p>
                <p className="font-sans text-base text-ink-mute">{t('waiter.notes.empty.subtitle')}</p>
              </div>
            ) : (
              <>
                {(emporterFilter === 'all' || emporterFilter === 'counter') &&
                  (emporterCards.length === 0 ? (
                    <p className="px-3 py-4 font-sans text-base text-ink-mute">{t('waiter.notes.online.emptyCounter')}</p>
                  ) : (
                    <div
                      data-testid="note-grid"
                      // auto-fill instead of a fixed `grid-cols-3`: measured live, that
                      // is 3 tracks from 360px up (103px at 360, 113 at 390, 120 at
                      // 412 — the spec's 114 at 390) and 2 fatter ones at 320px, where
                      // a third track would put the cards under the 100px floor and
                      // clip « 12 · 4 prs · 09:05 ».
                      className="grid grid-cols-[repeat(auto-fill,minmax(96px,1fr))] gap-2 p-3 content-start"
                    >
                      {emporterCards.map((card) => {
                        const pendingCount = pendingItemsCount(card.pendingValidation);
                        const unsentQty = readUnsentWaiterQty(card.sessionUuid);
                        const unsentBadgeId = unsentQty > 0 ? `note-card-unsent-${card.sessionUuid}` : undefined;
                        return (
                          // The card is a plain wrapper, not a button: the ⋮ transfer
                          // trigger and the pending badge are siblings of the card
                          // button, never nested interactive content inside it.
                          <div key={card.sessionUuid} className="relative">
                            <button
                              type="button"
                              data-testid={`note-card-${card.sessionUuid}`}
                              data-kind={card.kind}
                              data-label={card.label}
                              aria-describedby={unsentBadgeId}
                              onClick={() => navigateToSession(card.sessionUuid)}
                              className="w-full aspect-square min-h-[104px] bg-paper border border-hairline rounded-xl shadow-soft p-2.5 flex flex-col items-center justify-center gap-1"
                            >
                              <span
                                data-testid="note-card-label"
                                className="font-sans font-bold text-ink max-w-full text-[18px] line-clamp-2 text-center"
                              >
                                {card.label}
                              </span>
                              <span
                                data-testid="note-card-time"
                                className="font-sans text-base bg-ash rounded-md px-2 py-1 leading-none"
                              >
                                {formatTicketTime(card.openedAt)}
                              </span>
                              {/* Phone: no running total (04-ux-mobile-spec.md §1 — the
                                  card only has ~100px of content and the ticket header
                                  carries the total). Tablet cards have room for it. */}
                              <span
                                data-testid="note-card-total"
                                className="hidden sm:inline font-sans text-sm text-ink-mute"
                              >
                                <Price amountCents={priceToCents(card.runningTotal)} />
                              </span>
                            </button>
                            {pendingCount > 0 && (
                              <span
                                data-testid="note-card-pending"
                                role="img"
                                aria-label={t('waiter.notes.card.pending', { n: pendingCount })}
                                className="absolute -top-1.5 -start-1.5 min-w-[22px] h-[22px] px-1 rounded-full text-white font-sans text-[13px] font-bold flex items-center justify-center"
                                style={{ backgroundColor: 'var(--w-danger)' }}
                              >
                                {pendingCount}
                              </span>
                            )}
                            {unsentBadgeId && <UnsentCartBadge qty={unsentQty} id={unsentBadgeId} />}
                          </div>
                        );
                      })}
                    </div>
                  ))}

                {(emporterFilter === 'all' || emporterFilter === 'online') && (
                  <div data-testid="online-section" className="flex flex-col">
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-3 pt-3 pb-1">
                      <h2 className="font-sans text-base font-bold text-ink">
                        {t('waiter.notes.online.sectionTitle', { n: takeawayOnlineOrders.length })}
                      </h2>
                      {nextPickupOrder && (
                        <span className="font-sans text-sm text-ink-mute">
                          {t('waiter.notes.online.nextPickup', {
                            time: formatTicketTime(nextPickupOrder.scheduledAt as string),
                          })}
                        </span>
                      )}
                    </div>
                    {onlineOrdersError && (
                      <p
                        data-testid="online-section-error"
                        role="alert"
                        className="px-3 pb-2 font-sans text-sm font-semibold"
                        style={{ color: 'var(--w-danger)' }}
                      >
                        {t('waiter.notes.online.loadError')}
                      </p>
                    )}
                    {takeawayOnlineOrders.length === 0 ? (
                      <p className="px-3 pb-4 font-sans text-base text-ink-mute">{t('waiter.notes.online.emptyOnline')}</p>
                    ) : (
                      <div className="flex flex-col">
                        {takeawayOnlineOrders.map((order) => (
                          <OnlineOrderRow key={order.uuid} order={order} onTap={setOnlineOrderTarget} />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Emporter only. Sur place carried a « + Nouvelle table » here (spec
          2026-08-28 D3 kept it as the old app's habit), but the grid above
          already lists EVERY table and tapping a free tile opens it with the
          same covers stepper — a second door to the same room, worded as if
          it created a table. Gone; the ~72px it cost go back to the grid,
          and the scroll panel takes over the safe-area inset. */}
      {activeTab === 'emporter' && !showDelivery && (
        <footer
          data-testid="notes-footer"
          className="bg-paper border-t-2 border-hairline px-3 pt-2 waiter-safe-bottom"
        >
          <button
            type="button"
            data-testid="note-add"
            onClick={() => setCreateModalOpen(true)}
            className="w-full max-w-[420px] mx-auto h-14 rounded-btn bg-ink text-paper font-sans text-[18px] font-bold flex items-center justify-center gap-2"
          >
            <span aria-hidden="true" className="text-[26px] leading-none">
              +
            </span>
            {t('waiter.notes.addEmporter')}
          </button>
        </footer>
      )}

      {/* SC-971 — « Nouvelle réservation » a la meme place et la meme forme que
          « Nouvelle note » ci-dessus : c'est le meme geste, il ne doit pas
          s'ecrire deux fois dans le meme carnet. Et c'est la que le pouce
          arrive, sur un telephone tenu d'une main pendant le service. */}
      {activeTab === 'reservations' && (
        <footer
          data-testid="notes-footer"
          className="bg-paper border-t-2 border-hairline px-3 pt-2 waiter-safe-bottom"
        >
          <button
            type="button"
            data-testid="reservations-new"
            onClick={() => setNewResaOpen(true)}
            className="w-full max-w-[420px] mx-auto h-14 rounded-btn bg-ink text-paper font-sans text-[18px] font-bold flex items-center justify-center gap-2"
          >
            <span aria-hidden="true" className="text-[26px] leading-none">
              +
            </span>
            {t('waiter.notes.reservations.new')}
          </button>
        </footer>
      )}

      <NewReservationSheet
        open={newResaOpen}
        nowMs={nowMs}
        onClose={() => setNewResaOpen(false)}
        onSubmit={handleCreateReservation}
      />
      <NewReservationSheet
        open={editingResa !== null}
        nowMs={nowMs}
        initial={editingDraft}
        onClose={() => setEditingResa(null)}
        onSubmit={handleUpdateReservation}
      />

      <NoteCreateModals
        open={createModalOpen}
        token={token}
        takeawayNotes={takeawayNotes ?? []}
        onClose={() => setCreateModalOpen(false)}
        onCreated={handleCreated}
      />

      <OpenTableModal
        table={openTarget}
        token={token}
        onClose={() => {
          setOpenTarget(null);
          setSeatFor(null);
        }}
        onCreated={handleTableOpened}
        initialCovers={seatFor?.partySize}
        extraCandidates={sortWaiterTables(
          salleTables.filter((tb) => tb.session === null && tb.uuid !== openTarget?.uuid),
          'room',
        )}
        subtitle={
          seatFor
            ? t('waiter.notes.reservations.seatSubtitle', {
                name: seatFor.customerName,
                n: seatFor.partySize,
              })
            : undefined
        }
        submitLabel={seatFor ? t('waiter.notes.reservations.seatSubmit') : undefined}
      />

      <OnlineOrderDetailModal
        order={onlineOrderTarget}
        token={token}
        onClose={() => setOnlineOrderTarget(null)}
        showToast={showToast}
      />

      <Modal
        open={switchSheetOpen}
        onClose={() => setSwitchSheetOpen(false)}
        ariaLabel={t('waiter.switchSheet.title')}
        instant
      >
        {/* One scroller for the whole sheet, not one inside the roster: with
            the language row below it, a long roster on a 320x568 phone made
            the sum of the fixed parts taller than the dialog (`Modal` is
            `max-h-[calc(100dvh-24px)] overflow-hidden`), which clipped
            « Verrouiller l'écran » instead of scrolling to it. */}
        <div data-testid="waiter-switch-sheet" className="p-4 flex flex-col gap-3 min-h-0 overflow-y-auto">
          <h2 className="font-sans text-[18px] font-bold">{t('waiter.switchSheet.title')}</h2>
          {/* Fidélité — la seule action de service de la feuille, donc en
              tête : une longue liste de serveurs ne doit pas la pousser sous
              le pli. */}
          {loyaltyEnabled && (
            <button
              type="button"
              data-testid="loyalty-scan-row"
              aria-haspopup="dialog"
              onClick={() => {
                setSwitchSheetOpen(false);
                setLoyaltyScanOpen(true);
              }}
              className="shrink-0 h-12 flex items-center justify-center gap-2 rounded-btn bg-ink text-paper font-sans text-[18px] font-bold"
            >
              <ScanIcon />
              {t('waiter.loyaltyScan.title')}
            </button>
          )}
          {/* Le filet sépare le scan de ce qui suit : « le code d'accès sera
              demandé » vaut pour changer de serveur, pas pour scanner. */}
          {loyaltyEnabled && <div aria-hidden="true" className="shrink-0 border-t-2 border-hairline" />}
          <p className="font-sans text-base text-ink-mute">{t('waiter.switchSheet.pinNotice')}</p>
          <ul className="flex flex-col gap-2">
            {roster.map((name) => {
              const isCurrent = name === waiterName;
              return (
                <li key={name}>
                  <button
                    type="button"
                    data-testid="switch-waiter"
                    data-name={name}
                    aria-current={isCurrent ? 'true' : undefined}
                    onClick={() => (isCurrent ? setSwitchSheetOpen(false) : dispatch(startNameChange(name)))}
                    className="w-full min-h-[56px] flex items-center justify-between gap-3 rounded-btn border-2 border-ink bg-paper px-4 font-sans text-[18px] font-bold text-ink"
                  >
                    <span className="truncate">{name}</span>
                    {isCurrent && (
                      <span className="shrink-0 font-sans text-[13px] font-bold text-ink-mute">
                        {t('waiter.switchSheet.you')}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
          {/* The handheld has no other settings surface, so the device's
              language is picked here — beside the roster, above the two
              device actions. */}
          <WaiterLanguagePicker />
          {/* `shrink-0`: `h-12` is a flex-item main size, so on a 320x568
              phone these two were the ones that gave the extra rows their
              space — measured at 41px, under the 44px floor of §0. They now
              keep their height and the sheet scrolls instead. */}
          {/* SC-906 — la seule facon de retrouver l'installation une fois la
              bande du haut refermee. */}
          <InstallAppPrompt variant="row" />
          {/* SC-1044 — la ligne « Activer les notifications » est masquée à
              la demande (2026-09-29) : `push/NotificationsRow` reste en
              place, la remettre ici suffit à la rallumer. */}
          {/* SC-1082 — dernière ligne avant les deux actions d'appareil
              (verrouiller / annuler) : le seul endroit où « Signaler un bug »
              vit, sans prendre de place sur les écrans eux-mêmes. */}
          <BugReportRow
            onOpen={() => {
              setSwitchSheetOpen(false);
              setBugReportOpen(true);
            }}
          />
          <button
            type="button"
            data-testid="switch-lock"
            onClick={() => dispatch(setUiLocked(true))}
            className="shrink-0 h-12 rounded-btn border-2 border-ink bg-paper font-sans text-[18px] font-bold text-ink"
          >
            {t('waiter.switchSheet.lock')}
          </button>
          <button
            type="button"
            data-testid="switch-cancel"
            onClick={() => setSwitchSheetOpen(false)}
            className="shrink-0 h-12 rounded-btn font-sans text-[18px] font-bold underline text-ink"
          >
            {t('common.cancel')}
          </button>
        </div>
      </Modal>

      <BugReportSheet
        open={bugReportOpen}
        onClose={() => setBugReportOpen(false)}
        token={token}
        isOnline={isOnline}
      />

      <LoyaltyScanSheet open={loyaltyScanOpen} onClose={() => setLoyaltyScanOpen(false)} token={token} />

      <Modal
        open={transferSourceTableUuid !== null}
        onClose={closeTransfer}
        ariaLabel={t('waiter.overview.actionsTitle', { number: sheetLabel })}
        instant
      >
        {regroupPending !== null && transferSourceTable ? (
          (() => {
            const resultUuids = regroupResult(regroupPending.mode, transferSourceTable, regroupPending.picked, tablesByUuid);
            const absorbed = absorbedSessions(transferSourceTable, regroupPending.picked, tablesByUuid);
            const freed = freedUuids(regroupPending.mode, transferSourceTable, resultUuids);
            const added = resultUuids.filter((uuid) => !(transferSourceTable.session?.tableUuids ?? []).includes(uuid));
            const money = (value: string | undefined) => formatPrice(priceToCents(value ?? '0'));
            const mergeImpossible = regroupPending.picked.some((uuid) => {
              const tb = tablesByUuid.get(uuid);
              return tb !== undefined && mergeBlockReason(tb, transferSourceTable) !== null;
            });
            // SC-1007 — only « Réunir les notes » exists: when the server would
            // refuse it, say why and keep the confirm button off.
            const blocked = absorbed.length > 0 && mergeImpossible;
            return (
              <div data-testid="transfer-confirm" className="p-4 flex flex-col gap-3">
                <h2 className="font-sans text-[18px] font-bold">
                  {regroupPending.mode === 'merge'
                    ? t('waiter.overview.mergeConfirmTitle', { from: sheetLabel, to: labelOf(added) })
                    : t('waiter.overview.transferConfirmTitle', { from: sheetLabel, to: labelOf(resultUuids) })}
                </h2>
                <p className="font-sans text-base text-ink-mute">
                  {t('waiter.overview.regroupSourceLine', {
                    from: sheetLabel,
                    total: money(transferSourceTable.session?.runningTotal),
                    count: transferSourceTable.session?.roundCount ?? 0,
                  })}
                </p>
                {blocked ? (
                  <p
                    data-testid="regroup-merge-unavailable"
                    className="font-sans text-base font-semibold"
                    role="alert"
                    style={{ color: 'var(--w-danger)' }}
                  >
                    {t('waiter.overview.regroupMergeUnavailable')}
                  </p>
                ) : (
                  absorbed.map((other) => (
                    <p
                      key={other.uuid}
                      data-testid={`regroup-absorb-${other.uuid}`}
                      className="rounded-btn px-3 py-2 font-sans text-base font-semibold w-tile-ordered border-solid"
                    >
                      {t('waiter.overview.regroupAbsorbLine', {
                        label: other.tableLabel ?? labelOf(other.tableUuids ?? []),
                        total: money(other.runningTotal),
                        count: other.roundCount,
                      })}
                    </p>
                  ))
                )}
                <p data-testid="regroup-result" className="font-sans text-base font-bold">
                  {t('waiter.overview.regroupResultLine', { result: labelOf(resultUuids) })}
                </p>
                {freed.length > 0 && (
                  <p data-testid="regroup-freed" className="font-sans text-base text-ink-mute">
                    {t('waiter.overview.regroupFreedLine', { freed: labelOf(freed) })}
                  </p>
                )}
                {transferError && (
                  <p className="font-sans text-base font-semibold" role="alert" style={{ color: 'var(--w-danger)' }}>
                    {transferError}
                  </p>
                )}
                <div className="flex gap-2">
                  <button
                    type="button"
                    data-testid="transfer-confirm-cancel"
                    disabled={transferBusy}
                    onClick={() => setRegroupPending(null)}
                    className="flex-1 min-h-12 bg-paper border-2 border-ink rounded-btn px-4 font-sans text-[18px] font-bold text-ink disabled:opacity-50"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="button"
                    data-testid="transfer-confirm-ok"
                    disabled={transferBusy || blocked}
                    onClick={() => void confirmRegroup()}
                    className="flex-1 min-h-12 bg-hot-pink text-[var(--on-accent)] border-2 border-ink rounded-btn px-4 py-1 font-sans text-[18px] font-bold leading-tight disabled:opacity-50"
                  >
                    {transferBusy
                      ? t('waiter.overview.transferPending')
                      : regroupPending.mode === 'merge'
                        ? t('waiter.overview.mergeConfirmButton')
                        : t('waiter.overview.transferConfirmButton')}
                  </button>
                </div>
              </div>
            );
          })()
        ) : detachPendingTarget !== null ? (
          // SC-976 — design §5.1 « Séparer » confirmation.
          <div data-testid="detach-confirm" className="p-4 flex flex-col gap-3">
            <h2 className="font-sans text-[18px] font-bold">
              {t('waiter.overview.detachConfirmTitle', { number: detachPendingTarget.number })}
            </h2>
            <p className="font-sans text-base text-ink-mute">
              {t('waiter.overview.detachConfirmBody', {
                number: detachPendingTarget.number,
                anchor: transferSourceAnchorNumber,
              })}
            </p>
            {detachError && (
              <p className="font-sans text-base font-semibold" role="alert" style={{ color: 'var(--w-danger)' }}>
                {detachError}
              </p>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="detach-confirm-cancel"
                disabled={detachBusy}
                onClick={() => setDetachPendingTarget(null)}
                className="flex-1 h-12 bg-paper border-2 border-ink rounded-btn px-4 font-sans text-[18px] font-bold text-ink disabled:opacity-50"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                data-testid="detach-confirm-ok"
                disabled={detachBusy}
                onClick={() => void confirmDetach()}
                className="flex-1 h-12 bg-hot-pink text-[var(--on-accent)] border-2 border-ink rounded-btn px-4 font-sans text-[18px] font-bold disabled:opacity-50"
              >
                {detachBusy ? t('waiter.overview.detachPending') : t('waiter.overview.detachConfirmButton')}
              </button>
            </div>
          </div>
        ) : (
          <div
            data-testid="transfer-picker"
            // Review fix-T3-c P2#2 (Codex) — `Modal` caps its dialog at
            // `max-h-[calc(100dvh-24px)]` with `overflow-hidden` (by
            // design: it owns the FRAME, not what scrolls inside it — see
            // the switch sheet's own `min-h-0 overflow-y-auto` a few
            // hundred lines below). Ajouter/Séparer added two more
            // sections on top of covers-edit + transfer; on a short
            // screen (a 320×568 phone, or any phone once the on-screen
            // keyboard is up) the sheet used to clip past its own
            // bottom — with body scroll disabled by `Modal`, the
            // Annuler button and the tail of the transfer grid were
            // simply unreachable. `min-h-0` lets this flex child shrink
            // below its content size so `overflow-y-auto` can take over.
            className="p-4 flex flex-col gap-3 min-h-0 overflow-y-auto"
          >
            <h2 className="shrink-0 font-sans text-[18px] font-bold">
              {t('waiter.overview.actionsTitle', { number: sheetLabel })}
            </h2>
            <div data-testid="covers-edit" className="shrink-0 flex flex-wrap items-center justify-between gap-2">
              <span className="font-sans text-base font-semibold">{t('waiter.notes.createTable.covers')}</span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  data-testid="covers-edit-minus"
                  aria-label="diminuer"
                  disabled={coversDraft <= 1 || coversBusy}
                  onClick={() => setCoversDraft((c) => Math.max(1, c - 1))}
                  className="w-12 h-12 border-2 border-ink rounded-btn-sm font-sans text-xl bg-paper text-ink disabled:opacity-40"
                >
                  −
                </button>
                <span data-testid="covers-edit-value" className="font-mono font-bold text-[20px] min-w-[2ch] text-center">
                  {coversDraft}
                </span>
                <button
                  type="button"
                  data-testid="covers-edit-plus"
                  aria-label="augmenter"
                  disabled={coversDraft >= coversMax || coversBusy}
                  onClick={() => setCoversDraft((c) => Math.min(coversMax, c + 1))}
                  className="w-12 h-12 border-2 border-ink rounded-btn-sm bg-paper text-ink font-sans text-xl disabled:opacity-40"
                >
                  +
                </button>
                <button
                  type="button"
                  data-testid="covers-edit-save"
                  disabled={coversBusy || coversDraft === transferSourceTable?.session?.covers}
                  aria-busy={coversBusy}
                  onClick={() => void saveCovers()}
                  className="h-12 bg-ink text-paper rounded-btn px-3 font-sans text-base font-bold disabled:opacity-40"
                >
                  {t('waiter.overview.coversSave')}
                </button>
              </div>
            </div>
            {coversError && (
              <p className="shrink-0 font-sans text-base font-semibold" role="alert" style={{ color: 'var(--w-danger)' }}>
                {coversError}
              </p>
            )}
            {/* SC-976 — design §5.1 « Séparer » : jamais sur l'ancre elle-même
                (E-22) — soit UNE rattachée précise (tuile rattachée tapée),
                soit la liste des rattachées de la tablée (ancre tapée). */}
            {transferSourceIsMember ? (
              <>
                <h3 className="font-sans text-base font-bold border-t-2 border-hairline pt-3">
                  {t('waiter.overview.detachTitle')}
                </h3>
                <button
                  type="button"
                  data-testid={`detach-candidate-${transferSourceTable?.uuid}`}
                  onClick={() =>
                    transferSourceTable &&
                    setDetachPendingTarget({ uuid: transferSourceTable.uuid, number: transferSourceTable.number })
                  }
                  className="h-12 bg-paper border-2 border-ink rounded-btn px-4 font-sans text-[18px] font-bold text-ink text-start"
                >
                  {t('waiter.overview.detachAction', { number: transferSourceTable?.number ?? '' })}
                </button>
              </>
            ) : (
              /* SC-976 regroup (2026-09-16) — three tabs instead of stacked
                 sections: « Transférer » / « Réunir » share one picker (free
                 AND occupied tables, grouped by salle, painted with the salle
                 grid's state colours), « Séparer » only when there is a
                 rattachée to separate (never the anchor itself, E-22). A
                 member tile's ⋮ keeps its own « Séparer » above (P2#8). */
              <>
                <div
                  role="tablist"
                  aria-label={t('waiter.overview.regroupTabs')}
                  className="shrink-0 flex border-b-2 border-hairline mt-1"
                >
                  {regroupTabs.map((tab) => {
                    const active = tab === activeRegroupTab;
                    return (
                      <button
                        key={tab}
                        type="button"
                        role="tab"
                        id={`regroup-tab-${tab}`}
                        data-testid={`regroup-tab-${tab}`}
                        aria-selected={active}
                        aria-controls="regroup-panel"
                        onClick={() => {
                          if (active) return;
                          setRegroupTab(tab);
                          setRegroupPicked([]);
                          setTransferError(null);
                          setDetachError(null);
                        }}
                        className={`flex-1 h-12 -mb-[2px] border-b-4 font-sans text-[17px] ${
                          active ? 'border-ink text-ink font-bold' : 'border-transparent text-ink-mute font-medium opacity-70'
                        }`}
                      >
                        {t(REGROUP_TAB_LABEL[tab])}
                      </button>
                    );
                  })}
                </div>
                <div
                  id="regroup-panel"
                  role="tabpanel"
                  aria-labelledby={`regroup-tab-${activeRegroupTab}`}
                  data-testid={`regroup-panel-${activeRegroupTab}`}
                  // Only the picker's grid may shrink (and scroll); the short
                  // « Séparer » list keeps its height.
                  className={`flex flex-col gap-3 ${activeRegroupTab === 'detach' ? 'shrink-0' : 'flex-auto min-h-0'}`}
                >
                  {activeRegroupTab === 'detach' ? (
                    <>
                      <p className="font-sans text-base text-ink-mute">{t('waiter.overview.detachPickLabel')}</p>
                      <div className="grid grid-cols-[repeat(auto-fill,minmax(72px,1fr))] gap-2">
                        {transferSourceMembers.map((member) => (
                          <button
                            key={member.uuid}
                            type="button"
                            data-testid={`detach-candidate-${member.uuid}`}
                            onClick={() => setDetachPendingTarget(member)}
                            className="h-12 bg-paper border-2 border-ink rounded-btn px-2 font-sans text-[18px] font-bold text-ink truncate"
                          >
                            {member.number}
                          </button>
                        ))}
                      </div>
                    </>
                  ) : (
                    <>
                      {transferError && (
                        <p className="shrink-0 font-sans text-base font-semibold" role="alert" style={{ color: 'var(--w-danger)' }}>
                          {transferError}
                        </p>
                      )}
                      {transferSourceTable && (
                        <RegroupPicker
                          mode={regroupMode}
                          source={transferSourceTable}
                          tables={allTables}
                          areas={areas ?? []}
                          picked={regroupPicked}
                          onPick={setRegroupPicked}
                        />
                      )}
                      <button
                        type="button"
                        data-testid="transfer-submit"
                        disabled={regroupPicked.length === 0}
                        onClick={() => setRegroupPending({ mode: regroupMode, picked: regroupPicked })}
                        // `min-h-12` + wrap: « Déplacer vers 7+8+9+10+11 » needs
                        // two lines at 390px (same overflow OpenTableModal's
                        // merged submit had); the label must never spill.
                        className="shrink-0 min-h-12 bg-ink text-paper rounded-btn px-4 py-2 font-sans text-[18px] font-bold leading-tight disabled:opacity-40"
                      >
                        {regroupPicked.length === 0
                          ? t(
                              regroupMode === 'merge'
                                ? 'waiter.overview.mergeSubmitNone'
                                : 'waiter.overview.transferSubmitNone',
                            )
                          : t(
                              regroupMode === 'merge' ? 'waiter.overview.mergeSubmit' : 'waiter.overview.transferSubmit',
                              { label: regroupPickedLabel },
                            )}
                      </button>
                    </>
                  )}
                </div>
              </>
            )}
            <button
              type="button"
              data-testid="transfer-cancel"
              onClick={closeTransfer}
              className="shrink-0 h-12 bg-paper border-2 border-ink rounded-btn px-4 font-sans text-[18px] font-bold text-ink"
            >
              {t('common.cancel')}
            </button>
          </div>
        )}
      </Modal>
    </div>
  );
}
